import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Option } from '@spike/model';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import DropdownContainer from './DropdownContainer';
import styles from './Filter.module.scss';
import Footer from './Footer';
import Header from './Header';
import ListContent from './ListContent';
import useNonInitialEffect from '@versiondos/hooks';

export type FilterOption<T> = Option<T> & {
    disabled?: boolean;
    description?: string;
};

interface FilterProps<T> {
    options: Array<FilterOption<T>>;
    selectedOptions: Array<FilterOption<T>>;
    allLabel: string;
    quantityLabel: string;
    icon: IconDefinition;
    singleSelection?: boolean;
    showLabelOnMobile?: boolean;
    hideSearch?: boolean;
    className?: string;
    onChangeSelection?: (selected: Array<FilterOption<T>>) => void;
}

const getSelectableOptions = <T,>(options: Array<FilterOption<T>>): Array<FilterOption<T>> =>
    options.filter(option => !option.disabled);

export const Filter = <T,>(props: FilterProps<T>) => {
    const [expanded, setExpanded] = useState(false);

    const [collapsedSelection, setCollapsedSelection] = useState<Array<FilterOption<T>>>([]);

    const [selectedOptions, setSelectedOptions] = useState<Array<FilterOption<T>>>(() => {
        const selectableOptions = getSelectableOptions(props.options);
        return props.selectedOptions.length === 0
            ? [...selectableOptions]
            : getSelectableOptions(props.selectedOptions);
    });

    const [selectedAll, setSelectedAll] = useState(() => {
        const selectableOptions = getSelectableOptions(props.options);
        return (
            props.selectedOptions.length === 0 ||
            selectableOptions.every(option =>
                props.selectedOptions.map(selectedOption => selectedOption.id).includes(option.id)
            )
        );
    });

    const [searchText, setSearchText] = useState('');

    useNonInitialEffect(() => {
        const selectableOptions = getSelectableOptions(props.options);
        setSelectedOptions(
            props.selectedOptions.length === 0 ? [...selectableOptions] : getSelectableOptions(props.selectedOptions)
        );
        setSelectedAll(
            props.selectedOptions.length === 0 ||
                selectableOptions.every(option =>
                    props.selectedOptions.map(selectedOption => selectedOption.id).includes(option.id)
                )
        );
    }, [props.options, props.selectedOptions]);

    const options = Array.from(
        props.options
            .reduce<Map<T, FilterOption<T>>>((map, option) => {
                if (!map.has(option.id)) {
                    map.set(option.id, option);
                }
                return map;
            }, new Map())
            .values()
    ).filter(option => {
        if (searchText === '') {
            return true;
        } else {
            const tokens = searchText.toLocaleLowerCase().split(' ');
            return option.name
                .toLocaleLowerCase()
                .split(' ')
                .some(nameToken => tokens.some(token => nameToken.startsWith(token)));
        }
    });

    const selectableOptions = getSelectableOptions(options);

    const buttonLabel = selectedAll
        ? props.allLabel
        : selectedOptions.length === 1
        ? selectedOptions[0]?.name?.substring(0, 16) ?? `1 ${props.quantityLabel}`
        : `${selectedOptions.length} ${props.quantityLabel}`;

    const clickButtonHandler = () => {
        if (!expanded) {
            setCollapsedSelection(selectedOptions);
        }

        setExpanded(prev => !prev);
    };

    const selectionChangeHandler = (selectedIds: Array<T>) => {
        const nextSelected = selectableOptions.filter(option => selectedIds.includes(option.id));
        setSelectedOptions(nextSelected);
        setSelectedAll(
            selectableOptions.length > 0 && selectableOptions.every(option => selectedIds.includes(option.id))
        );
    };

    const selectAllHandler = (selected: boolean) => {
        setSelectedOptions(selected ? [...selectableOptions] : []);
        setSelectedAll(selected);
    };

    const applyHandler = () => {
        setExpanded(false);
        setCollapsedSelection([]);
        props.onChangeSelection?.([...selectedOptions]);
    };

    const clickAwayHandler = (event: FocusEvent | MouseEvent | TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedOptions(collapsedSelection);
        const selectableAllOptions = getSelectableOptions(props.options);
        setSelectedAll(
            collapsedSelection.length === 0 ||
                selectableAllOptions.every(option =>
                    collapsedSelection.map(selectedOption => selectedOption.id).includes(option.id)
                )
        );
        setExpanded(false);
    };

    const header =
        !props.singleSelection && props.options.length > 1 ? (
            <Header
                allLabel={props.allLabel}
                selectedAll={selectedAll}
                hideSearch={props.hideSearch}
                onClose={() => setExpanded(false)}
                onSearch={props.hideSearch ? undefined : setSearchText}
                onSelectAll={selectAllHandler}
            />
        ) : null;

    const footer = (
        <Footer
            onApply={applyHandler}
            disabled={selectedOptions.length === 0}
        />
    );

    const list = (
        <ClickAwayListener onClickAway={clickAwayHandler}>
            <div>
                <DropdownContainer
                    header={header}
                    footer={footer}
                >
                    <ListContent
                        options={options}
                        selectedIds={selectedOptions.map(option => option.id)}
                        singleSelection={props.singleSelection}
                        onChange={selectionChangeHandler}
                    />
                </DropdownContainer>
            </div>
        </ClickAwayListener>
    );

    return (
        <div className={clsx(styles.container, props.className, { [styles.labelOnMobile]: props.showLabelOnMobile })}>
            <Button
                variant={'outlined'}
                label={buttonLabel}
                leftIcon={props.icon}
                size="large"
                onClick={clickButtonHandler}
                className={styles.button}
            />
            <div
                className={styles.mobileButton}
                onClick={clickButtonHandler}
            >
                <FontAwesomeIcon icon={props.icon} />
            </div>
            {expanded && list}
        </div>
    );
};

export default Filter;
